class PremiumProcessTabs extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.tabs = Array.from(this.querySelectorAll('[data-process-tab]'));
    this.panels = Array.from(this.querySelectorAll('[data-process-panel]'));
    this.steps = Array.from(this.querySelectorAll('[data-process-step]'));
    this.visibleSteps = new Set();
    this.activeVideoStep = null;
    this.videoSelectionFrame = null;
    this.status = this.querySelector('[data-process-status]');
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.isVisible = !('IntersectionObserver' in window);

    this.onTabClick = this.handleTabClick.bind(this);
    this.onTabKeydown = this.handleTabKeydown.bind(this);
    this.onBlockSelect = this.handleBlockSelect.bind(this);
    this.onRouteResize = this.scheduleMobileRoutes.bind(this);
    window.addEventListener('resize', this.onRouteResize, { passive: true });
    if ('ResizeObserver' in window) {
      this.routeObserver = new ResizeObserver(this.onRouteResize);
      this.routeObserver.observe(this);
    }

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', this.onTabClick);
      tab.addEventListener('keydown', this.onTabKeydown);
    });
    document.addEventListener('shopify:block:select', this.onBlockSelect);

    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(this.handleIntersection, { rootMargin: '100px', threshold: 0 });
      this.observer.observe(this);
      this.dataset.stepObserver = 'true';
      this.stepObserver = new IntersectionObserver(this.handleStepIntersection, {
        rootMargin: '160px 0px 160px 0px',
        threshold: 0,
      });
    } else {
      this.dataset.inView = 'true';
      this.steps.forEach((step) => {
        step.dataset.stepRevealed = 'true';
        step.dataset.stepVisible = 'true';
      });
    }

    const selected = this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || this.tabs[0];
    if (selected) this.activate(selected.dataset.processTab, { focus: false, announce: false, initial: true });
  }

  disconnectedCallback() {
    this.tabs?.forEach((tab) => {
      tab.removeEventListener('click', this.onTabClick);
      tab.removeEventListener('keydown', this.onTabKeydown);
    });
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
    this.observer?.disconnect();
    this.stepObserver?.disconnect();
    this.routeObserver?.disconnect();
    window.removeEventListener('resize', this.onRouteResize);
    cancelAnimationFrame(this.routeFrame);
    cancelAnimationFrame(this.videoSelectionFrame);
    this.pauseAllVideos({ release: true });
    this.initialized = false;
  }

  handleTabClick(event) {
    this.activate(event.currentTarget.dataset.processTab, { focus: false, announce: true });
  }

  handleTabKeydown(event) {
    const current = this.tabs.indexOf(event.currentTarget);
    let next = current;

    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (current + 1) % this.tabs.length;
    else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (current - 1 + this.tabs.length) % this.tabs.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = this.tabs.length - 1;
    else return;

    event.preventDefault();
    const tab = this.tabs[next];
    this.activate(tab.dataset.processTab, { focus: true, announce: true });
  }

  activate(group, options = {}) {
    const { focus = false, announce = false, initial = false } = options;
    const activeTab = this.tabs.find((tab) => tab.dataset.processTab === group);
    const activePanel = this.ensureProcessPanel(group);
    if (!activeTab || !activePanel) return;

    this.panels = Array.from(this.querySelectorAll('[data-process-panel]'));
    this.steps = Array.from(this.querySelectorAll('[data-process-step]'));

    this.tabs.forEach((tab) => {
      const selected = tab === activeTab;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
    });

    this.panels.forEach((panel) => {
      if (panel === activePanel) return;
      panel.classList.remove('is-active');
      panel.hidden = true;
      this.pausePanelVideos(panel);
    });

    activePanel.hidden = false;
    if (initial) {
      activePanel.classList.add('is-active');
    } else {
      activePanel.classList.remove('is-active');
      requestAnimationFrame(() => requestAnimationFrame(() => activePanel.classList.add('is-active')));
    }

    if (focus) activeTab.focus({ preventScroll: true });
    if (!initial) {
      activeTab.scrollIntoView({ behavior: this.reducedMotion.matches ? 'auto' : 'smooth', block: 'nearest', inline: 'center' });
    }
    this.activeGroup = group;
    this.observePanelSteps(activePanel);
    this.scheduleMobileRoutes();

    if (this.isVisible) this.scheduleActiveStepVideo();

    if (announce && this.status) {
      const label = activeTab.textContent.trim();
      const count = activePanel.querySelectorAll('[data-process-step]').length;
      this.status.textContent = `${label}: ${count} process ${count === 1 ? 'step' : 'steps'}`;
    }
  }

  ensureProcessPanel(group) {
    const existingPanel = this.querySelector(`[data-process-panel="${group}"]`);
    if (existingPanel) return existingPanel;

    const template = this.querySelector(`template[data-process-panel-template="${group}"]`);
    if (!template) return null;

    const fragment = template.content.cloneNode(true);
    const panel = fragment.querySelector(`[data-process-panel="${group}"]`);
    if (!panel) return null;

    template.replaceWith(fragment);
    return panel;
  }

  activatePanelVideos(panel) {
    if (!panel) return;
    this.scheduleActiveStepVideo();
  }

  activateStepVideos(step) {
    step.querySelectorAll('video').forEach((video) => {
      this.ensureVideoPoster(video);
      this.ensureVideoLoaded(video);
      video.autoplay = true;
      video.loop = true;
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.preload = 'none';

      const playVideo = () => {
        delete video.dataset.playPending;
        if (this.activeVideoStep === step && this.isVisible) video.play().catch(() => {});
      };
      if (video.readyState >= 2) playVideo();
      else if (video.dataset.playPending !== 'true') {
        video.dataset.playPending = 'true';
        video.addEventListener('canplay', playVideo, { once: true });
      }
    });
  }

  deactivateStepVideos(step, { release = false } = {}) {
    step?.querySelectorAll('video').forEach((video) => {
      video.pause();
      delete video.dataset.playPending;
      if (release) this.releaseVideo(video);
    });
  }

  scheduleActiveStepVideo() {
    cancelAnimationFrame(this.videoSelectionFrame);
    this.videoSelectionFrame = requestAnimationFrame(() => this.updateActiveStepVideo());
  }

  updateActiveStepVideo() {
    const panel = this.panels.find((item) => item.dataset.processPanel === this.activeGroup);
    if (!this.isVisible || !panel || panel.hidden) {
      if (this.activeVideoStep) this.deactivateStepVideos(this.activeVideoStep, { release: true });
      this.activeVideoStep = null;
      return;
    }

    const viewportCenter = window.innerHeight / 2;
    const candidates = Array.from(this.visibleSteps).filter((step) => step.closest('[data-process-panel]') === panel);
    const nextStep = candidates
      .filter((step) => step.querySelector('video'))
      .sort((a, b) => {
        const aRect = a.getBoundingClientRect();
        const bRect = b.getBoundingClientRect();
        const aDistance = Math.abs(aRect.top + aRect.height / 2 - viewportCenter);
        const bDistance = Math.abs(bRect.top + bRect.height / 2 - viewportCenter);
        return aDistance - bDistance;
      })[0] || null;

    if (nextStep === this.activeVideoStep) return;
    if (this.activeVideoStep) this.deactivateStepVideos(this.activeVideoStep, { release: true });
    this.activeVideoStep = nextStep;
    if (nextStep) this.activateStepVideos(nextStep);
  }

  observePanelSteps(panel) {
    if (!panel) return;

    this.stepObserver?.disconnect();
    const steps = panel.querySelectorAll('[data-process-step]');

    if (this.stepObserver) {
      steps.forEach((step) => this.stepObserver.observe(step));
      return;
    }

    steps.forEach((step) => {
      step.dataset.stepRevealed = 'true';
      step.dataset.stepVisible = 'true';
    });
  }

  ensureVideoLoaded(video) {
    if (video.dataset.sourcesLoaded === 'true') return;

    let hasSource = false;
    video.querySelectorAll('source[data-src]').forEach((source) => {
      const originalSource = source.dataset.originalSrc || source.dataset.src;
      source.dataset.originalSrc = originalSource;
      source.src = this.getOptimizedVideoSource(originalSource);
      source.removeAttribute('data-src');
      hasSource = true;
    });

    if (hasSource) {
      video.dataset.sourcesLoaded = 'true';
      video.load();
    }
  }

  ensureVideoPoster(video) {
    const poster = video.dataset.originalPoster || video.dataset.poster || video.getAttribute('poster');
    if (poster) {
      video.dataset.originalPoster = poster;
      const optimizedPoster = this.getOptimizedPosterSource(poster);
      if (video.poster !== optimizedPoster) video.poster = optimizedPoster;
      delete video.dataset.poster;
    }

    const fallback = video.querySelector('img[data-src]');
    if (fallback) {
      fallback.src = this.getOptimizedPosterSource(fallback.dataset.src);
      fallback.removeAttribute('data-src');
    }
  }

  prepareStepPosters(step) {
    step?.querySelectorAll('video').forEach((video) => this.ensureVideoPoster(video));
  }

  getOptimizedPosterSource(sourceUrl) {
    if (!sourceUrl || !/_small\.jpg/.test(sourceUrl)) return sourceUrl;
    return sourceUrl.replace(/_small\.jpg/, window.matchMedia('(max-width: 749px)').matches ? '_medium.jpg' : '_large.jpg');
  }

  getOptimizedVideoSource(sourceUrl) {
    if (!sourceUrl || !/\.HD-1080p-[\d.]+Mbps-/.test(sourceUrl)) return sourceUrl;
    const mobile = window.matchMedia('(max-width: 749px)').matches;
    const rendition = mobile ? '.SD-480p-1.5Mbps-' : '.HD-720p-4.5Mbps-';
    return sourceUrl.replace(/\.HD-1080p-[\d.]+Mbps-/, rendition);
  }

  releaseVideo(video) {
    let released = false;
    video.querySelectorAll('source[src]').forEach((source) => {
      source.dataset.src = source.dataset.originalSrc || source.getAttribute('src');
      source.removeAttribute('src');
      released = true;
    });
    if (!released) return;
    video.dataset.sourcesLoaded = 'false';
    video.load();
  }

  pausePanelVideos(panel) {
    panel.querySelectorAll('[data-process-step]').forEach((step) => this.deactivateStepVideos(step, { release: true }));
    if (this.activeVideoStep?.closest('[data-process-panel]') === panel) this.activeVideoStep = null;
  }

  pauseAllVideos({ release = false } = {}) {
    this.steps.forEach((step) => this.deactivateStepVideos(step, { release }));
    if (release) this.activeVideoStep = null;
  }

  handleBlockSelect(event) {
    const block = event.target.closest?.('[data-process-group]');
    if (!block || !this.contains(block)) return;
    const group = block.dataset.processGroup;
    if (group) this.activate(group, { focus: false, announce: false });
  }

  handleIntersection = (entries) => {
    this.isVisible = entries[0]?.isIntersecting ?? true;
    if (this.isVisible) {
      this.dataset.inView = 'true';
      const panel = this.panels.find((item) => item.dataset.processPanel === this.activeGroup);
      if (panel && !this.stepObserver) {
        this.visibleSteps = new Set(panel.querySelectorAll('[data-process-step]'));
        this.scheduleActiveStepVideo();
      } else if (panel) {
        this.scheduleActiveStepVideo();
      }
    } else {
      this.pauseAllVideos({ release: true });
    }
  };

  handleStepIntersection = (entries) => {
    entries.forEach(({ target, isIntersecting }) => {
      target.dataset.stepVisible = String(isIntersecting);
      if (isIntersecting) {
        this.visibleSteps.add(target);
        this.prepareStepPosters(target);
        target.dataset.stepRevealed = 'true';
      } else {
        this.visibleSteps.delete(target);
        if (target === this.activeVideoStep) this.deactivateStepVideos(target, { release: true });
      }
    });
    this.scheduleActiveStepVideo();
  };

  scheduleMobileRoutes() {
    cancelAnimationFrame(this.routeFrame);
    this.routeFrame = requestAnimationFrame(() => this.updateMobileRoutes());
  }

  updateMobileRoutes() {
    if (!window.matchMedia('(max-width: 749px)').matches) return;
    const panel = this.panels.find((item) => item.dataset.processPanel === this.activeGroup);
    if (!panel || panel.hidden) return;
    const steps = Array.from(panel.querySelectorAll('[data-process-step]'));

    steps.forEach((step, index) => {
      const next = steps[index + 1];
      const route = step.querySelector('.ppts__route');
      const svg = route?.querySelector('.ppts__mobile-route-line');
      const media = step.querySelector('.ppts__media');
      const nextMedia = next?.querySelector('.ppts__media');
      if (!svg || !media || !nextMedia) return;

      // Layout offsets exclude the entrance transforms, so the curve stays
      // attached as each step reveals and when a phone changes orientation.
      const width = route.clientWidth;
      const height = route.clientHeight;
      if (!width || !height) return;
      const start = media.offsetLeft + media.offsetWidth / 2;
      const end = next.offsetLeft - step.offsetLeft + nextMedia.offsetLeft + nextMedia.offsetWidth / 2;
      const bend = Math.min(parseFloat(getComputedStyle(this).getPropertyValue('--ppts-route-width')) || 85, width / 3);
      const sameSide = Math.abs(end - start) < 8;
      const direction = start < width / 2 ? 1 : -1;
      const control1 = sameSide ? start + direction * bend : start;
      const control2 = sameSide ? end + direction * bend : end;
      const path = `M${start} 0 C${control1} ${height * 0.56} ${control2} ${height * 0.44} ${end} ${height}`;
      svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
      svg.querySelectorAll('path').forEach((line) => line.setAttribute('d', path));
      svg.querySelector('.ppts__mobile-route-spark').style.offsetPath = `path('${path}')`;
      route.style.setProperty('--ppts-route-start-x', `${start}px`);
    });
  }
}

if (!customElements.get('premium-process-tabs')) {
  customElements.define('premium-process-tabs', PremiumProcessTabs);
}
